import { billingRequest } from "./billing-client";

export interface RecurringInvoiceDto {
  id: string;
  number: string;
  status: string;
  frequency: string;
  interval: number;
  currency: string;
  total: string;
  paidAmount: string;
  issueDate: string;
  dueDate: string | null;
  nextIssueDate: string | null;
  createdAt: string;
  customer: { id: string; code: string; displayName: string };
}

export interface CreditNoteDto {
  id: string;
  number: string;
  status: string;
  currency: string;
  total: string;
  paymentMode: string | null;
  creditNoteDate: string;
  createdAt: string;
  customer: { id: string; code: string; displayName: string };
  relatedInvoice: { id: string; number: string } | null;
}

export interface CreateRecurringInvoicePayload {
  customerId: string;
  frequency: "DAILY" | "WEEKLY" | "MONTHLY" | "QUARTERLY" | "YEARLY";
  interval?: number;
  issueDate: string;
  dueDate?: string;
  total: number;
  description?: string;
}

export interface CreditNoteDetailDto extends CreditNoteDto {
  referenceNumber: string | null;
  subtotal: string;
  taxTotal: string;
  notes: string | null;
  lines: Array<{
    id: string;
    lineNumber: number;
    description: string;
    quantity: string;
    unitPrice: string;
    taxRate: string;
    lineTotal: string;
    productId: string | null;
  }>;
}

export interface CreateCreditNotePayload {
  customerId: string;
  relatedInvoiceId?: string;
  referenceNumber?: string;
  creditNoteDate?: string;
  paymentMode?: string;
  notes?: string;
  lines: Array<{
    productId?: string;
    description?: string;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
  }>;
}

export const recurringInvoicesApi = {
  list: (params?: { page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.limit) q.set("limit", String(params.limit));
    const query = q.toString();
    return billingRequest<{ items: RecurringInvoiceDto[]; meta: { total: number } }>(
      `/recurring-invoices${query ? `?${query}` : ""}`,
    );
  },
  create: (payload: CreateRecurringInvoicePayload) =>
    billingRequest<RecurringInvoiceDto>("/recurring-invoices", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  get: (id: string) => billingRequest<RecurringInvoiceDto>(`/recurring-invoices/${id}`),
  update: (id: string, payload: Partial<CreateRecurringInvoicePayload & { status?: string; description?: string }>) =>
    billingRequest<RecurringInvoiceDto>(`/recurring-invoices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  pause: (id: string) =>
    billingRequest<RecurringInvoiceDto>(`/recurring-invoices/${id}/pause`, { method: "POST" }),
  resume: (id: string) =>
    billingRequest<RecurringInvoiceDto>(`/recurring-invoices/${id}/resume`, { method: "POST" }),
  generateInvoice: (id: string) =>
    billingRequest<{ recurring: RecurringInvoiceDto; invoice: { id: string; number: string } }>(
      `/recurring-invoices/${id}/generate-invoice`,
      { method: "POST" },
    ),
};

export const creditNotesApi = {
  list: (params?: { page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.limit) q.set("limit", String(params.limit));
    const query = q.toString();
    return billingRequest<{ items: CreditNoteDto[]; meta: { total: number } }>(
      `/credit-notes${query ? `?${query}` : ""}`,
    );
  },
  create: (payload: CreateCreditNotePayload) =>
    billingRequest<CreditNoteDto>("/credit-notes", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  get: (id: string) => billingRequest<CreditNoteDetailDto>(`/credit-notes/${id}`),
  update: (id: string, payload: Partial<CreateCreditNotePayload>) =>
    billingRequest<CreditNoteDetailDto>(`/credit-notes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  apply: (id: string, invoiceId: string) =>
    billingRequest<CreditNoteDetailDto>(`/credit-notes/${id}/apply`, {
      method: "POST",
      body: JSON.stringify({ invoiceId }),
    }),
};
